import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import attributeActions from "../../../store/actions/items/attributes/attribute.actions";
import { useNavigate } from "react-router-dom";
import { SkeletonTable } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const GetAttributesPageComponent = ({ attributeActions }) => {
  const navigate = useNavigate();
  const [attributes, setAttributes] = useState([]); // Store attributes
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState(""); // Search term
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch attributes when the page loads or search/pagination changes
  useEffect(() => {
    fetchAttributes();
  }, [pagination.pageNumber, pagination.pageSize, searchTerm]);

  const fetchAttributes = async () => {
    setIsLoading(true);
    setError(null);

    const queryParams = {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      searchTerm,
    };

    try {
      const response = await attributeActions.getAttributes(queryParams);
      setAttributes(response.payload.items);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.payload.totalCount,
        totalPages: response.payload.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching attributes:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination((prev) => ({ ...prev, pageNumber: 1 })); // Reset to page 1 when searching
  };

  const handlePageChange = (newPageNumber) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPageNumber }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1, // Reset to page 1 when page size changes
    }));
  };

  const editAttributeClicked = (attributeId) => {
    navigate(`/attributes/edit/${attributeId}`);
  };

  const attributeValuesClicked = (attributeId) => {
    navigate(`/attributes/${attributeId}/values`);
  };

  return (
    <div>
      <h2>Attributes List</h2>

      <div>
        <input type="text" placeholder="Search attributes..." value={searchTerm} onChange={handleSearchChange} />
      </div>

      <div>
        <label>Page Size:</label>
        <select value={pagination.pageSize} onChange={handlePageSizeChange}>
          <option value="1">1</option>
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>

      {isLoading ? (
        showSkeleton && <SkeletonTable rows={10} cols={3} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Attribute Name</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attributes.map((attribute) => (
              <tr key={attribute.id}>
                <td>{attribute.name}</td>
                <td>{attribute.description}</td>
                <td>
                  <div>
                    <button onClick={()=>{editAttributeClicked(attribute.id)}}>Edit</button>
                    <button onClick={()=>{attributeValuesClicked(attribute.id)}}>Values</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div>
        <button disabled={pagination.pageNumber === 1} onClick={() => handlePageChange(pagination.pageNumber - 1)}>
          Previous
        </button>
        <span>
          Page {pagination.pageNumber} of {pagination.totalPages}
        </span>
        <button disabled={pagination.pageNumber === pagination.totalPages} onClick={() => handlePageChange(pagination.pageNumber + 1)}>
          Next
        </button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    attributeActions: bindActionCreators(attributeActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(GetAttributesPageComponent);
